import { useMemo, useState } from 'react';
import { AlertCircle, KeyRound, Loader2, MoreHorizontal, Pencil, Plus, Search, ShieldCheck, UserCheck, UserX, Users } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/context/authContext';
import { useRolesOverview } from '@/features/roles/hooks/useRoles';
import { normalizeLoginIdentifier } from '@/lib/loginIdentifier';
import * as repo from '../data/usersRepository';
import { CreateUserDialog } from '../components/CreateUserDialog';
import { EditUserDialog } from '../components/EditUserDialog';
import { TempPasswordDialog } from '../components/TempPasswordDialog';
import { UserRolesDialog } from '../components/UserRolesDialog';
import { scopeText } from '../utils/scope';
import { useUsersMutation, useUsersOverview } from '../hooks/useUsers';
import type { TempCredentials, UserOverview } from '../types';

const ALL = '__all__';
type StatusFilter = typeof ALL | 'active' | 'inactive';

const normalize = (v: string) => v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

function formatLastLogin(iso: string | null): string {
  if (!iso) return 'Nunca';
  return new Date(iso).toLocaleString('es-BO', { dateStyle: 'short', timeStyle: 'short' });
}

export function UsersPage() {
  const { user: me } = useAuth();
  const { data: users = [], isLoading, isError, refetch } = useUsersOverview();
  const { data: roles = [] } = useRolesOverview();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [roleFilter, setRoleFilter] = useState<string>(ALL);

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rolesId, setRolesId] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<TempCredentials | null>(null);
  const [resetting, setResetting] = useState<UserOverview | null>(null);
  const [toggling, setToggling] = useState<UserOverview | null>(null);

  const reset = useUsersMutation((id: string) => repo.resetPassword(id));
  const toggle = useUsersMutation(
    ({ id, active }: { id: string; active: boolean }) => repo.setUserActive(id, active),
  );

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    const qUser = normalizeLoginIdentifier(search);
    return users.filter((u) => {
      if (status === 'active' && !u.is_active) return false;
      if (status === 'inactive' && u.is_active) return false;
      if (roleFilter !== ALL && !u.roles.some((r) => r.role_id === roleFilter)) return false;
      if (!q) return true;
      const haystack = normalize([u.full_name, u.username, u.display_email, u.minister_name, u.minister_adb_code].filter(Boolean).join(' '));
      return haystack.includes(q) || (!!qUser && (u.username ?? '').includes(qUser));
    });
  }, [users, search, status, roleFilter]);

  const editing = users.find((u) => u.id === editingId) ?? null;
  const rolesUser = users.find((u) => u.id === rolesId) ?? null;

  const confirmReset = () => {
    if (!resetting) return;
    const target = resetting;
    reset.mutate(target.id, {
      onSuccess: (result) => {
        setResetting(null);
        setCredentials({ name: target.full_name ?? '', username: target.username ?? '', password: result.temp_password });
      },
    });
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold"><Users className="size-6" /> Usuarios</h1>
          <p className="text-sm text-muted-foreground">Cuentas de acceso al sistema, sus roles y su estado.</p>
        </div>
        <Button onClick={() => setCreating(true)}><Plus className="mr-2 size-4" /> Nuevo usuario</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, usuario, CI o ministro" aria-label="Buscar usuarios" />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
          <SelectTrigger className="w-40" aria-label="Estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="inactive">Inactivos</SelectItem>
            <SelectItem value={ALL}>Todos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-56" aria-label="Rol"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos los roles</SelectItem>
            {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground" aria-live="polite">{filtered.length} de {users.length}</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center text-sm text-muted-foreground">
            <AlertCircle className="size-6 text-destructive" /> No se pudieron cargar los usuarios.
            <Button variant="outline" size="sm" onClick={() => void refetch()}>Reintentar</Button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Ningún usuario coincide con los filtros.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Ministro</TableHead>
                  <TableHead className="w-36">Último acceso</TableHead>
                  <TableHead className="w-12"><span className="sr-only">Acciones</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u.id} className={u.is_active ? '' : 'opacity-60'}>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{u.full_name}</span>
                        {!u.is_active && <Badge variant="outline">Inactivo</Badge>}
                        {u.must_change_password && <Badge variant="secondary">Contraseña temporal</Badge>}
                        {u.id === me?.id && <Badge variant="secondary">Tú</Badge>}
                      </div>
                      <p className="font-mono text-xs text-muted-foreground">{u.username ?? '—'}</p>
                      {u.display_email && <p className="text-xs text-muted-foreground">{u.display_email}</p>}
                    </TableCell>
                    <TableCell>
                      {u.roles.length === 0 ? <span className="text-xs text-muted-foreground">Sin roles</span> : (
                        <div className="flex flex-wrap gap-1.5">
                          {u.roles.map((r) => (
                            <Badge key={r.assignment_id} variant="secondary" title={scopeText(r)}>
                              {r.role_name}{r.scope !== 'NATIONAL' && ` · ${r.district_name ?? r.region_name}`}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {u.minister_id ? <>{u.minister_name} <span className="text-xs text-muted-foreground">· {u.minister_adb_code}</span></> : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-muted-foreground">{formatLastLogin(u.last_sign_in_at)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label={`Acciones de ${u.full_name}`}><MoreHorizontal className="size-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setEditingId(u.id)}><Pencil className="mr-2 size-4" /> Editar datos</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setRolesId(u.id)}><ShieldCheck className="mr-2 size-4" /> Roles</DropdownMenuItem>
                          <DropdownMenuItem disabled={u.id === me?.id} onSelect={() => setResetting(u)}><KeyRound className="mr-2 size-4" /> Restablecer contraseña</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {u.is_active ? (
                            <DropdownMenuItem disabled={u.id === me?.id} className="text-destructive focus:text-destructive" onSelect={() => setToggling(u)}>
                              <UserX className="mr-2 size-4" /> Desactivar
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onSelect={() => setToggling(u)}><UserCheck className="mr-2 size-4" /> Reactivar</DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CreateUserDialog open={creating} onOpenChange={setCreating} onCreated={setCredentials} />
      <EditUserDialog user={editing} onOpenChange={(open) => !open && setEditingId(null)} />
      <UserRolesDialog user={rolesUser} onOpenChange={(open) => !open && setRolesId(null)} />
      <TempPasswordDialog credentials={credentials} onClose={() => setCredentials(null)} />

      <AlertDialog open={!!resetting} onOpenChange={(open) => !open && setResetting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Restablecer la contraseña de {resetting?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se genera una contraseña temporal nueva y la anterior deja de funcionar. Deberá cambiarla al entrar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmReset(); }} disabled={reset.isPending}>
              {reset.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              Restablecer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toggling} onOpenChange={(open) => !open && setToggling(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {toggling?.is_active ? `¿Desactivar a ${toggling?.full_name}?` : `¿Reactivar a ${toggling?.full_name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toggling?.is_active
                ? 'No podrá iniciar sesión ni ver datos desde ahora. Su historial se conserva y puedes reactivarlo cuando quieras.'
                : 'Podrá iniciar sesión de nuevo con su contraseña actual.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={toggling?.is_active ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              onClick={(e) => {
                e.preventDefault();
                if (toggling) toggle.mutate({ id: toggling.id, active: !toggling.is_active }, { onSuccess: () => setToggling(null) });
              }}
              disabled={toggle.isPending}
            >
              {toggle.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              {toggling?.is_active ? 'Desactivar' : 'Reactivar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
